import { useState } from "react";
import { Box, Divider, FormControlLabel, Grid, Typography } from "@mui/material";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import { ByzMuiDateTimePicker } from "src/components/core-components/mui/inputs/ByzMuiDateTimePicker";
import ByzSelectListPopupInput from "src/components/core-components/mui/inputs/ByzSelectListPopupInput";
import { ByzMuiCheckBox } from "src/components/core-components/mui/ByzMuiCheckBox";
import ByzMuiRadioButton from "src/components/core-components/mui/buttons/ByzMuiRadioButton";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import { useTranslate } from "src/locales";

const OperationDetailsSection = () => {
    const { t } = useTranslate();

    const [formData, setFormData] = useState({
        areaVerification: '',
        markedArea1: '',
        markedArea2: '',
        markingOnPatient: false,
        markingOnFilmPatient: false,
        premature: false,
        allergy: false,
        markingRejection: false,
        acknowledgment: false,
        patientGuardian: '',
        patientGuardianDate: new Date(),
        makingMarking: '',
        makingMarkingDate: new Date(),
    });

    const handleInputChange = (field: string, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    return (
        <Box
            sx={{
                border: "1px solid #ccc",
                borderRadius: 2,
                p: 2,
                display: "flex",
                flexDirection: "column",
                height: "100%",
                boxSizing: "border-box",
            }}
        >
            <Box sx={{ backgroundColor: "#63B4FF33", borderRadius: "8px", p: 1, mb: 2 }}>
                <Typography fontWeight={600} color="#2979C3">
                    {t("safety-surgery.operation.detail")}
                </Typography>
            </Box>

            <Grid container spacing={2} alignItems="center" mb={2}>
                <Grid item xs={12} sm={3}>
                    <ByzMuiTypography isTitle>
                        {t("safety-surgery.area-verification")}
                    </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} sm={9}>
                    <Grid container spacing={2}>
                        <Grid item xs={6} md={3}>
                            <ByzMuiRadioButton
                                name="area-verification"
                                value="right"
                                checked={formData.areaVerification === 'right'}
                                onChange={(value) => handleInputChange('areaVerification', value)}
                                typographyText={t('safety-surgery.operation.right')}
                                layout="row"
                            />
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <ByzMuiRadioButton
                                name="area-verification"
                                value="left"
                                checked={formData.areaVerification === 'left'}
                                onChange={(value) => handleInputChange('areaVerification', value)}
                                typographyText={t('safety-surgery.operation.left')}
                                layout="row"
                            />
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <ByzMuiRadioButton
                                name="area-verification"
                                value="two-sides"
                                checked={formData.areaVerification === 'two-sides'}
                                onChange={(value) => handleInputChange('areaVerification', value)}
                                typographyText={t('safety-surgery.operation.two-sides')}
                                layout="row"
                            />
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <ByzMuiRadioButton
                                name="area-verification"
                                value="not-marked"
                                checked={formData.areaVerification === 'not-marked'}
                                onChange={(value) => handleInputChange('areaVerification', value)}
                                typographyText={t('safety-surgery.operation.not-marked')}
                                layout="row"
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            <Grid container spacing={2} alignItems="center" mb={1}>
                <Grid item xs={12} md={3}>
                    <ByzMuiTypography isTitle>
                        {t('safety-surgery.operation.marked-area')}
                    </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={9}>
                    <ByzMuiTextField
                        fullWidth
                        placeholder={t('safety-surgery.operation.marked-area')}
                        value={formData.markedArea1}
                        onChange={(e) => handleInputChange('markedArea1', e.target.value)}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={2} alignItems="center" mb={2}>
                <Grid item xs={12} md={3}>
                    <ByzMuiTypography isTitle>
                        {t('safety-surgery.operation.marked-area')}
                    </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={9}>
                    <ByzMuiTextField
                        fullWidth
                        placeholder={t('safety-surgery.operation.marked-area')}
                        value={formData.markedArea2}
                        onChange={(e) => handleInputChange('markedArea2', e.target.value)}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={2} alignItems="center" mb={2}>
                <Grid item xs={12} md={3}>
                    <ByzMuiTypography isTitle>
                        {t('safety-surgery.operation.situations')}
                    </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} sm={9} md={9}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} display="flex" alignItems="center">
                            <FormControlLabel
                                control={
                                    <ByzMuiCheckBox
                                        checked={formData.markingOnPatient}
                                        onChange={(checked) => handleInputChange('markingOnPatient', checked)}
                                    />
                                }
                                label={t('safety-surgery.operation.marking-on-the-patient')}
                                sx={{ marginLeft: 0, gap: 1 }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} display="flex" alignItems="center">
                            <FormControlLabel
                                control={
                                    <ByzMuiCheckBox
                                        checked={formData.markingOnFilmPatient}
                                        onChange={(checked) => handleInputChange('markingOnFilmPatient', checked)}
                                    />
                                }
                                label={t('safety-surgery.operation.marking-on-the-film-patient')}
                                sx={{ marginLeft: 0, gap: 1 }}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            <Divider sx={{ borderColor: "black", borderBottomWidth: "2px", my: 2 }} />

            <Grid container spacing={2} alignItems="center" mb={2}>
                <Grid item xs={12} md={3}>
                    <ByzMuiTypography isTitle>
                        {t('safety-surgery.operation.reason')}
                    </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} sm={9} md={9}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={4} display="flex" alignItems="center">
                            <FormControlLabel
                                control={
                                    <ByzMuiCheckBox
                                        checked={formData.premature}
                                        onChange={(checked) => handleInputChange('premature', checked)}
                                    />
                                }
                                label={t('safety-surgery.operation.premature')}
                                sx={{ marginLeft: 0, gap: 1 }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4} display="flex" alignItems="center">
                            <FormControlLabel
                                control={
                                    <ByzMuiCheckBox
                                        checked={formData.allergy}
                                        onChange={(checked) => handleInputChange('allergy', checked)}
                                    />
                                }
                                label={t('safety-surgery.operation.allergy')}
                                sx={{ marginLeft: 0, gap: 1 }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4} display="flex" alignItems="center">
                            <FormControlLabel
                                control={
                                    <ByzMuiCheckBox
                                        checked={formData.markingRejection}
                                        onChange={(checked) => handleInputChange('markingRejection', checked)}
                                    />
                                }
                                label={t('safety-surgery.operation.marking-rejection')}
                                sx={{ marginLeft: 0, gap: 1 }}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            <Typography variant="body2" sx={{ mt: 2, mb: 1, pl: 2 }}>
                {t('safety-surgery.operation.not')}
            </Typography>

            <Grid container>
                <Grid item xs={12} display="flex" alignItems="flex-start" sx={{ pl: 4 }}>
                    <FormControlLabel
                        control={
                            <ByzMuiCheckBox
                                checked={formData.acknowledgment}
                                onChange={(checked) => handleInputChange('acknowledgment', checked)}
                            />
                        }
                        label={t('safety-surgery.operation.not2')}
                        sx={{ marginLeft: 0 }}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={2} mt={2} alignItems="center">
                <Grid item xs={12} sm={3}>
                    <ByzMuiTypography isTitle sx={{ textAlign: 'left' }}>
                        {t('safety-surgery.operation.patient-guardian-legal')}
                    </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <ByzMuiTextField
                        sx={{ width: 220 }}
                        placeholder={t('safety-surgery.operation.patient-guardian-legal')}
                        value={formData.patientGuardian}
                        onChange={(e) => handleInputChange('patientGuardian', e.target.value)}
                    />
                </Grid>
                {/* DateTimePicker */}
                <Grid item xs={12} sm={4}>
                    <Box sx={{ width: '100%' }}>
                        <ByzMuiDateTimePicker label={t("date")} value={new Date()} />
                    </Box>
                </Grid>
            </Grid>

            <Grid container spacing={2} mt={2} alignItems="flex-start">
                <Grid item xs={12} sm={3}>
                    <ByzMuiTypography isTitle sx={{ textAlign: 'left' }}>
                        {t('safety-surgery.operation.making-marking')}
                    </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} sm={4}>
                <ByzSelectListPopupInput
                        label={t("safety-surgery.operation.making-marking")}
                        url=""
                        displayField="name"
                        sx={{ width: 220 }}
                    />
                </Grid>

                <Grid item xs={12} sm={4}>
                    <Box sx={{ width: '100%' }}>
                        <ByzMuiDateTimePicker label={t("date")} value={new Date()} />
                    </Box>
                </Grid>
            </Grid>

            <Typography variant="caption" display="block" mt={2}>
                {t('safety-surgery.operation.not3')}
            </Typography>
        </Box>
    );
};

export default OperationDetailsSection;



